import type { Node } from "reactflow";
import { INSIGHT_CONTAINER_NODE_TYPES, type InsightNodeData, type InsightSemanticNodeType } from "./topologyNodes";

/**
 * CTX-167 "hierarchy-aware navigation" — bounds the number of VPC/VCN,
 * availability-group, and subnet *cards* mounted at once, on top of
 * `eniDisclosure.ts`'s existing per-subnet/per-VPC ENI cap. CTX-128 only
 * bounded ENIs; a large real account (e.g. DISH staging) still mounted every
 * VPC/AZ/subnet simultaneously, so ReactFlow framed an unreadable
 * whole-account graph. This module never talks to the network and never
 * mutates its `nodes` input (every node that moves/resizes/changes type is a
 * fresh object) — it is a pure presentation-layer projection over an
 * already-built ReactFlow node list (`toReactFlowNodes`'s output), applied
 * *before* `applyEniDisclosure` so that function only ever has to consider
 * subnets this module decided are actually visible.
 *
 * Three sibling scope levels are bounded the same way (one "focused" branch
 * stays fully expanded, the rest collapse to compact summary cards, and an
 * over-the-limit remainder folds behind one "+N" overflow card):
 *  - VPC/VCN, among the VPCs sharing one parent (region/AZ/account boundary).
 *  - Availability group, among the AZ-kind nodes nested directly inside the
 *    one focused VPC — some digests place the AZ dimension *inside* the VPC
 *    boundary rather than as subnets' own attribute; a VPC with no such
 *    nested AZ nodes skips this level entirely (falls back straight to its
 *    own subnet children), matching the ticket's "no AZ dimension" case.
 *  - Subnet *count* (not a subnet's own content — that stays `eniDisclosure`'s
 *    job) among the direct subnet children of whichever host (the focused AZ,
 *    or the focused VPC when there's no AZ level) is currently expanded.
 *
 * A branch with only one sibling at a given level (the overwhelmingly common
 * case for a small/demo digest) never collapses — there is nothing to
 * collapse it *against* — so this module is a no-op (returns the input
 * array, same reference) for every existing single-VPC/single-AZ fixture,
 * preserving CTX-113/CTX-128's established full-detail rendering exactly.
 */

export const DEFAULT_VISIBLE_VPC_LIMIT = 12;
export const DEFAULT_VISIBLE_AZ_LIMIT = 8;
export const DEFAULT_VISIBLE_SUBNET_LIMIT = 24;
export const DEFAULT_VISIBLE_REGIONAL_LIMIT = 16;
/** Visible cap for an expanded VPC's route-table grid before the rest fold behind one "+N" overflow card (gateways are never capped — each is drawn exactly once in the tier above). */
export const DEFAULT_VISIBLE_SHELF_LIMIT = 8;

/** The leaf-card types that make up a VPC's connectivity shelf — bounded and dense-repacked as one band (see the shelf pass in `applyHierarchyDisclosure`). */
const SHELF_NODE_TYPES: ReadonlySet<string> = new Set(["insightRouteTable", "insightGateway"]);

/** Route-table card height in the shelf grid — a compact horizontal card (icon + name + id); its gateway associations render as family icon chips on its border. */
export const SHELF_ROUTE_TABLE_HEIGHT = 56;
/** First table row's vertical origin inside the VPC — below the container's own label/id/CIDR header text, so the tables' border chips never overlap the VPC name. */
export const SHELF_TABLE_ORIGIN_Y = 104;
/** Uniform box for every gateway-tier pill, regardless of the ELK size each gateway family arrived with — mismatched tier cards read as broken. */
export const GATEWAY_TIER_CARD_WIDTH = 208;
export const GATEWAY_TIER_CARD_HEIGHT = 56;
/** Clearance between the gateway tier (drawn OUTSIDE the VPC, above its top border) and the border itself. */
/**
 * Connectivity tray (2026-08-04): the gateway tier is enclosed in a labeled
 * band whose bottom edge sits flush on the VPC's top border — belonging by
 * containment instead of mere proximity ("those pills float above the VPC
 * but that tells me nothing").
 */
/** Chip-sized AZ summary — a single line of "name + N subnets + M ENIs"; anything taller/wider is padding ("demasiado grandes para solo mostrar la cantidad de ENIs"). */
export const AZ_SUMMARY_CARD_WIDTH = 250;
export const AZ_SUMMARY_CARD_HEIGHT = 40;
export const GATEWAY_TRAY_LABEL_HEIGHT = 20;
export const GATEWAY_TRAY_PAD_X = 12;
export const GATEWAY_TRAY_PAD_BOTTOM = 10;
/** Total vertical band a non-empty tier occupies above the VPC border. */
const gatewayTrayBandHeight = (tierHeight: number): number =>
	tierHeight > 0 ? GATEWAY_TRAY_LABEL_HEIGHT + tierHeight + GATEWAY_TRAY_PAD_BOTTOM : 0;

/** Fixed footprint for a collapsed VPC/AZ compact summary card, and for the "+N" overflow card standing in for the rest — deliberately small and uniform so the repacked overview grid is dense and legible regardless of how large the original ELK boxes were. Exported so `adaptiveViewport.ts` can size its viewport-driven capacity math against the exact same card footprint this module actually renders, instead of a second, possibly-drifting copy of these numbers. */
export const OVERVIEW_CARD_WIDTH = 220;
/** Tall enough for the summary card's full stack: header pill overhang + name + native id + CIDR + counts row + warning badge. */
export const OVERVIEW_CARD_HEIGHT = 126;
export const OVERVIEW_GRID_COLUMNS = 4;
export const OVERVIEW_GRID_GAP_X = 20;
export const OVERVIEW_GRID_GAP_Y = 20;
/** Left/top origin for a repacked sibling group, relative to its shared `parentId` — leaves room for the parent's own floating header label. */
export const OVERVIEW_ORIGIN_X = 24;
export const OVERVIEW_ORIGIN_Y = 48;
/** Vertical gap between the compact-card grid and the one fully-expanded (kept) sibling stacked beneath it. */
const KEPT_SECTION_GAP = 32;
/** Padding added around a refit container's children bounding box — mirrors the visual breathing room `topologyNodes.tsx`'s container cards already reserve for their floating header/badges. */
const CONTAINER_PADDING_RIGHT = 32;
const CONTAINER_PADDING_BOTTOM = 32;
/** Synthetic key (never a real node id) reserved in a repack's position map for the "+N" overflow card's slot in the compact grid. */
const OVERFLOW_SLOT_KEY = "__overflow__";
/** Footprint of one attachment node (circular family icon + label, count badge past 1) drawn above a collapsed VPC card — the member gateways' ids live in its tooltip and in the sidebar list it opens. */
const VPC_ATTACHMENT_NODE_WIDTH = 44;
const VPC_ATTACHMENT_NODE_HEIGHT = 52;
const VPC_ATTACHMENT_NODE_GAP = 8;
/** Clearance between the nodes and the card so their labels never sit under the card's own floating "VPC" pill (which overhangs the top border). */
const VPC_ATTACHMENT_PILL_CLEARANCE = 22;
/** Non-VPC top-level container types that can appear as a VPC's sibling directly under the shared account/region/provider node — swept into the same VPC-level repack (CTX-173) so their position moves with the compacted VPC cluster instead of retaining a stale, pre-collapse ELK coordinate. */
const OVERVIEW_SIBLING_CONTAINER_TYPES: ReadonlySet<InsightSemanticNodeType> = new Set(["insightRegionalArea", "insightOtherArea"]);

export interface HierarchyFocusState {
	/** `null` = nothing focused yet (compact-cards-only account/region overview). Ignored (treated as focused) when there is only one VPC total. */
	focusedVpcId: string | null;
	/** `null` = the focused VPC's AZ groups (if any) are all compact. Ignored when the focused VPC has zero or one nested AZ group. */
	focusedAzId: string | null;
	/** VPCs kept fully expanded alongside the focused one instead of collapsing when a different VPC is focused ("unless explicitly pinned"). */
	pinnedVpcIds: ReadonlySet<string>;
	/** AZ groups kept fully expanded alongside the focused AZ. */
	pinnedAzIds: ReadonlySet<string>;
	/** User clicked "+N more VPCs" — bypasses `visibleVpcLimit` entirely for this render. */
	vpcsExpanded: boolean;
	/** User clicked "+N more availability groups" within the focused VPC. */
	azsExpanded: boolean;
	/** User clicked "+N more subnets" within the focused VPC/AZ. */
	subnetsExpanded: boolean;
	/** User clicked "+N more regional resources". */
	regionalExpanded: boolean;
	/** User clicked "+N more route tables & gateways" on an expanded VPC's connectivity shelf. */
	shelfExpanded: boolean;
}

export const DEFAULT_HIERARCHY_FOCUS: HierarchyFocusState = {
	focusedVpcId: null,
	focusedAzId: null,
	pinnedVpcIds: new Set(),
	pinnedAzIds: new Set(),
	vpcsExpanded: false,
	azsExpanded: false,
	subnetsExpanded: false,
	regionalExpanded: false,
	shelfExpanded: false,
};

export interface HierarchyDisclosureOptions {
	visibleVpcLimit?: number;
	visibleAzLimit?: number;
	visibleSubnetLimit?: number;
	visibleRegionalLimit?: number;
	visibleShelfLimit?: number;
	/** Columns used by every compact-grid repack (VPC/AZ summary cards, and the subnet/regional survivor+overflow grid) — CTX-173: viewport-driven callers pass an adaptive value instead of the fixed `OVERVIEW_GRID_COLUMNS` default. */
	overviewColumns?: number;
	/** Search-matched VPCs whose branch must stay fully expanded this render, without persisting into `focusedVpcId`/`pinnedVpcIds` (CTX-167 "search-driven reveal"). */
	forceExpandedVpcIds?: ReadonlySet<string>;
	/** Search-matched AZ groups, same contract as `forceExpandedVpcIds`. */
	forceExpandedAzIds?: ReadonlySet<string>;
	/** Search-matched subnets that must render even if beyond the subnet-count cap. */
	forceVisibleSubnetIds?: ReadonlySet<string>;
	/** Search-matched regional-service ids that must render even if beyond the regional cap. */
	forceVisibleRegionalIds?: ReadonlySet<string>;
}

export interface HierarchyDisclosureResult {
	nodes: Node<InsightNodeData>[];
	/** The VPC id actually treated as focused this render (state's own value, or the sole VPC when there's only one). Drives the breadcrumb trail and the focus-change `fitView` effect. */
	effectiveFocusedVpcId: string | null;
	/** The AZ id actually treated as focused this render, `null` when the focused VPC has no nested AZ dimension at all. */
	effectiveFocusedAzId: string | null;
	/** Whether the focused VPC has any nested AZ-kind children — lets the canvas skip rendering an AZ breadcrumb segment entirely per the ticket's "no AZ dimension" fallback. */
	hasAzDimension: boolean;
}

interface Box {
	x: number;
	y: number;
	width: number;
	height: number;
}

function boxOf(node: Node<InsightNodeData>): Box {
	return {
		x: node.position.x,
		y: node.position.y,
		width: typeof node.width === "number" ? node.width : OVERVIEW_CARD_WIDTH,
		height: typeof node.height === "number" ? node.height : OVERVIEW_CARD_HEIGHT,
	};
}

interface ScopeCounts {
	subnetCount: number;
	eniCount: number;
	routeTableCount: number;
	warningCount: number;
	errorCount: number;
	/** VPC-level connection resources drawn as icon badges on the VPC card's top border — true attachments (IGW, TGW attachment, peering, DX) plus NAT (subnet-resident, but a VPC-level egress function), later referenced by the route tables. */
	attachments: { igw: number; nat: number; tgw: number; peering: number; directConnect: number };
}

function emptyCounts(): ScopeCounts {
	return {
		subnetCount: 0,
		eniCount: 0,
		routeTableCount: 0,
		warningCount: 0,
		errorCount: 0,
		attachments: { igw: 0, nat: 0, tgw: 0, peering: 0, directConnect: 0 },
	};
}

const IGW_KINDS = new Set(["igw", "internetGateway", "eigw", "egress-only-igw", "egressOnlyInternetGateway"]);
const NAT_KINDS = new Set(["nat", "natGateway"]);
const TGW_KINDS = new Set(["tgw", "transitGateway"]);
const PEERING_KINDS = new Set(["peering", "vpcPeeringConnection"]);
const DIRECT_CONNECT_KINDS = new Set(["drg", "dx", "direct-connect", "direct_connect", "directConnectGateway"]);

export type VpcAttachmentFamily = "igw" | "nat" | "tgw" | "peering" | "directConnect";

/** Which VPC-attachment family a gateway kind/type belongs to, `null` for gateway kinds that aren't VPC-level attachments (e.g. a VPC endpoint). Shared with `ResourceDetailPanel` so the sidebar's per-family gateway list can never drift from the nodes drawn on the canvas. */
export function attachmentFamilyOf(kind: string): VpcAttachmentFamily | null {
	if (IGW_KINDS.has(kind)) return "igw";
	if (NAT_KINDS.has(kind)) return "nat";
	if (TGW_KINDS.has(kind)) return "tgw";
	if (PEERING_KINDS.has(kind)) return "peering";
	if (DIRECT_CONNECT_KINDS.has(kind)) return "directConnect";
	return null;
}

/** Tallies every descendant of `rootId` (exclusive) by semantic type, for a collapsed VPC/AZ's compact summary card — computed from the *full* pre-collapse tree so the count is honest even though the descendants themselves are about to be dropped from the visible set. */
function countDescendants(rootId: string, childrenByParent: Map<string, Node<InsightNodeData>[]>): ScopeCounts {
	const counts = emptyCounts();
	const stack = [...(childrenByParent.get(rootId) ?? [])];
	while (stack.length > 0) {
		const node = stack.pop() as Node<InsightNodeData>;
		const data = node.data ?? {};
		switch (node.type) {
			case "insightSubnet":
				counts.subnetCount += 1;
				break;
			case "insightEni": {
				counts.eniCount += 1;
				if (data.status === "warning") counts.warningCount += 1;
				if (data.status === "error") counts.errorCount += 1;
				if (data.securityGroupsUnavailable) counts.warningCount += 1;
				break;
			}
			case "insightRouteTable":
				counts.routeTableCount += 1;
				break;
			case "insightGateway": {
				const kind = String(data.kind ?? "");
				if (IGW_KINDS.has(kind)) counts.attachments.igw += 1;
				else if (NAT_KINDS.has(kind)) counts.attachments.nat += 1;
				else if (TGW_KINDS.has(kind)) counts.attachments.tgw += 1;
				else if (PEERING_KINDS.has(kind)) counts.attachments.peering += 1;
				else if (DIRECT_CONNECT_KINDS.has(kind)) counts.attachments.directConnect += 1;
				break;
			}
			default:
				break;
		}
		stack.push(...(childrenByParent.get(node.id) ?? []));
	}
	return counts;
}

/** Every descendant id of `rootId`, exclusive, for removing a collapsed branch from the visible set. */
function collectDescendantIds(rootId: string, childrenByParent: Map<string, Node<InsightNodeData>[]>): string[] {
	const ids: string[] = [];
	const stack = [...(childrenByParent.get(rootId) ?? [])];
	while (stack.length > 0) {
		const node = stack.pop() as Node<InsightNodeData>;
		ids.push(node.id);
		stack.push(...(childrenByParent.get(node.id) ?? []));
	}
	return ids;
}

interface RepackResult {
	positions: Map<string, Box>;
	bounds: { width: number; height: number };
}

/**
 * Repacks one sibling group sharing a `parentId` into a dense compact-card
 * grid (collapsed entries + an optional "+N" overflow slot) followed by the
 * kept (fully expanded) entries stacked below, each retaining its own
 * original width/height (only its top-level x/y moves — every descendant
 * position stays parent-relative and so moves with it for free). This is the
 * "dedicated compact overview layout" the ticket asks for instead of hiding
 * nodes while leaving the ELK-computed gaps between their original
 * positions.
 */
function repackSiblingGroup(
	collapsedIds: string[],
	hasOverflow: boolean,
	keptEntries: Array<{ id: string; width: number; height: number; topHeadroom?: number }>,
	columns: number = OVERVIEW_GRID_COLUMNS,
	originY: number = OVERVIEW_ORIGIN_Y,
	/** Extra vertical space reserved ABOVE each compact row — room for the attachment nodes drawn above collapsed VPC cards. Zero everywhere else keeps the grid byte-for-byte identical. */
	rowHeadroom = 0,
	/** Compact slot height — VPC summaries keep the default; AZ summaries pass their shorter card. */
	slotHeight: number = OVERVIEW_CARD_HEIGHT,
	/** Compact slot width — same contract as `slotHeight`. */
	slotWidth: number = OVERVIEW_CARD_WIDTH
): RepackResult {
	const positions = new Map<string, Box>();
	let cursorY = originY;
	const compactSlots = [...collapsedIds, ...(hasOverflow ? [OVERFLOW_SLOT_KEY] : [])];

	if (compactSlots.length > 0) {
		compactSlots.forEach((id, index) => {
			const col = index % columns;
			const row = Math.floor(index / columns);
			positions.set(id, {
				x: OVERVIEW_ORIGIN_X + col * (slotWidth + OVERVIEW_GRID_GAP_X),
				y: cursorY + rowHeadroom + row * (slotHeight + OVERVIEW_GRID_GAP_Y + rowHeadroom),
				width: slotWidth,
				height: slotHeight,
			});
		});
		const rows = Math.ceil(compactSlots.length / columns);
		cursorY += rows * (slotHeight + OVERVIEW_GRID_GAP_Y + rowHeadroom) - OVERVIEW_GRID_GAP_Y + KEPT_SECTION_GAP;
	}

	keptEntries.forEach((entry) => {
		// Per-entry headroom reserves the band an expanded VPC's gateway tier
		// occupies ABOVE its border — without it, stacked open VPCs put their
		// tier pills on top of the previous sibling's bottom edge.
		cursorY += entry.topHeadroom ?? 0;
		positions.set(entry.id, { x: OVERVIEW_ORIGIN_X, y: cursorY, width: entry.width, height: entry.height });
		cursorY += entry.height + KEPT_SECTION_GAP;
	});

	const columnsUsed = Math.min(compactSlots.length, columns);
	const compactWidth = columnsUsed > 0 ? columnsUsed * (slotWidth + OVERVIEW_GRID_GAP_X) - OVERVIEW_GRID_GAP_X : 0;
	const keptWidth = keptEntries.reduce((max, entry) => Math.max(max, entry.width), 0);
	const totalHeight = cursorY - (keptEntries.length > 0 ? KEPT_SECTION_GAP : 0);

	return {
		positions,
		bounds: { width: Math.max(compactWidth, keptWidth), height: Math.max(totalHeight - OVERVIEW_ORIGIN_Y, 0) },
	};
}

/**
 * Dense grid repack for a sibling set that (unlike a collapsed VPC/AZ) has
 * no compact "summary card" substitute — a subnet or regional-service
 * overflow only caps sibling *count*, so every survivor still renders at its
 * own real width/height. Without this, survivors keep whatever x/y the
 * server's ELK pass gave them (spread across the *original*, uncollapsed
 * sibling count's footprint) and the synthetic "+N" overflow card has no
 * sensible position of its own — both are exactly the "stale, far-away
 * coordinate" failure CTX-173 already fixed for the VPC/regional-area case,
 * reappearing one level down for a real (single- or few-VPC) massive
 * account's subnet/regional-service count. Row height is the tallest entry
 * actually placed in that row, so non-uniform card sizes never overlap.
 */
function repackVariableGrid(
	entries: Array<{ id: string; width: number; height: number }>,
	columns: number = OVERVIEW_GRID_COLUMNS,
	originY: number = OVERVIEW_ORIGIN_Y,
	/**
	 * When true, rows are filled to a width budget instead of a fixed number of
	 * entries. Use it whenever the entries' widths genuinely differ — see
	 * `rowWidthBudget` for why a fixed column count wrecks that case.
	 */
	packByWidth = false
): Map<string, Box> {
	const positions = new Map<string, Box>();
	const budget = packByWidth ? rowWidthBudget(entries, columns) : Number.POSITIVE_INFINITY;
	let cursorX = OVERVIEW_ORIGIN_X;
	let cursorY = originY;
	let rowHeight = 0;
	let inRow = 0;

	entries.forEach((entry) => {
		const wouldExceedBudget = inRow > 0 && cursorX - OVERVIEW_ORIGIN_X + entry.width > budget;
		const rowIsFull = !packByWidth && inRow === columns;
		if (wouldExceedBudget || rowIsFull) {
			cursorX = OVERVIEW_ORIGIN_X;
			cursorY += rowHeight + OVERVIEW_GRID_GAP_Y;
			rowHeight = 0;
			inRow = 0;
		}
		positions.set(entry.id, { x: cursorX, y: cursorY, width: entry.width, height: entry.height });
		cursorX += entry.width + OVERVIEW_GRID_GAP_X;
		rowHeight = Math.max(rowHeight, entry.height);
		inRow += 1;
	});
	return positions;
}

/**
 * Width one packed row may reach: `columns` worth of the WIDEST entry.
 *
 * A fixed column count only produces a tidy grid when every card is the same
 * size. Subnet cards are not: `cardLayout.ts` sizes each one around its own ENI
 * grid, so a one-ENI subnet is ~210px and a three-ENI subnet ~600px. Four per
 * row then yields rows of wildly different widths — the container has to hug the
 * widest of them, leaving the narrow rows trailing dead space, which is the
 * "desastre" a DISH AZ with ~25 subnets shows. Budgeting by width instead fits
 * more narrow cards per row and fewer wide ones, so the block stays rectangular
 * and dense whatever mix of sizes the AZ holds.
 */
function rowWidthBudget(entries: Array<{ width: number }>, columns: number): number {
	const widest = entries.reduce((max, entry) => Math.max(max, entry.width), 0);
	return columns * (widest + OVERVIEW_GRID_GAP_X) - OVERVIEW_GRID_GAP_X;
}

/**
 * Reading order for a set of subnet cards: by CIDR, numerically, falling back to
 * the display name and finally the id so the result is always total and stable.
 *
 * The digest lists subnets in discovery order, which to a network engineer looks
 * like no order at all — `net-aws-keysight-mgmt` next to `rt-internal-need-to-
 * delete` next to `net-cs-vrouter`. Address order is the one arrangement that
 * lets someone scan an AZ and see which ranges exist and which are missing.
 */
function compareSubnetsForReading(a: Node<InsightNodeData>, b: Node<InsightNodeData>): number {
	const keyA = cidrSortKey(a.data?.cidr);
	const keyB = cidrSortKey(b.data?.cidr);
	if (keyA !== keyB) return keyA - keyB;
	const nameA = String(a.data?.label ?? a.id);
	const nameB = String(b.data?.label ?? b.id);
	return nameA.localeCompare(nameB) || a.id.localeCompare(b.id);
}

/** An IPv4 CIDR as a sortable integer (prefix length breaks ties between equal networks). Anything unparseable — absent, IPv6, malformed — sorts last rather than scrambling the addressed ones. */
function cidrSortKey(cidr: unknown): number {
	if (typeof cidr !== "string") return Number.MAX_SAFE_INTEGER;
	const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/.exec(cidr.trim());
	if (!match) return Number.MAX_SAFE_INTEGER;
	const octets = [match[1], match[2], match[3], match[4]].map(Number);
	if (octets.some((octet) => octet > 255)) return Number.MAX_SAFE_INTEGER;
	const prefix = Number(match[5]);
	if (prefix > 32) return Number.MAX_SAFE_INTEGER;
	const network = ((octets[0] << 24) >>> 0) + (octets[1] << 16) + (octets[2] << 8) + octets[3];
	return network * 64 + prefix;
}

/** Groups `ids` by their shared `parentId` (root/parentless ids bucket under the sentinel `"__root__"`), preserving each group's original relative order. */
function groupByParent(ids: string[], byId: Map<string, Node<InsightNodeData>>): Map<string, string[]> {
	const groups = new Map<string, string[]>();
	ids.forEach((id) => {
		const parentId = byId.get(id)?.parentId ?? "__root__";
		const list = groups.get(parentId) ?? [];
		list.push(id);
		groups.set(parentId, list);
	});
	return groups;
}

/** `id` and every ancestor above it via the `parentId` chain — used to mark every container that might need refitting when one of its (possibly deeply nested) descendants changes size. */
function idAndAncestors(id: string | undefined, byId: Map<string, Node<InsightNodeData>>): string[] {
	const chain: string[] = [];
	let current = id;
	const seen = new Set<string>();
	while (current && byId.has(current) && !seen.has(current)) {
		chain.push(current);
		seen.add(current);
		current = byId.get(current)?.parentId;
	}
	return chain;
}

function buildVpcSummaryNode(vpcNode: Node<InsightNodeData>, counts: ScopeCounts, box: Box): Node<InsightNodeData> {
	return {
		id: vpcNode.id,
		type: "insightVpcSummary",
		parentId: vpcNode.parentId,
		position: { x: box.x, y: box.y },
		width: box.width,
		height: box.height,
		extent: vpcNode.extent,
		style: { width: box.width, height: box.height },
		data: { ...vpcNode.data, summaryCounts: counts, scopeId: vpcNode.id, scopeLevel: "vpc" },
	};
}

/** Display order and labels of the attachment families drawn above a collapsed VPC card. */
const VPC_ATTACHMENT_FAMILIES: Array<{ key: keyof ScopeCounts["attachments"]; label: string }> = [
	{ key: "igw", label: "IGW" },
	{ key: "nat", label: "NAT" },
	{ key: "tgw", label: "TGW" },
	{ key: "peering", label: "PCX" },
	{ key: "directConnect", label: "DX" },
];

/** Every gateway descendant of `vpcId`, grouped by attachment family, as chips carrying each gateway's real node id, name, and native id — the node tooltip and the sidebar list read these. */
export function collectVpcAttachmentGateways(
	vpcId: string,
	childrenByParent: Map<string, Node<InsightNodeData>[]>
): Map<VpcAttachmentFamily, Array<{ id: string; label: string; nativeId?: string }>> {
	const byFamily = new Map<VpcAttachmentFamily, Array<{ id: string; label: string; nativeId?: string }>>();
	// Breadth-first in sibling order, so the pills/tooltip list gateways in
	// the digest's own (server-decided) order rather than a reversed walk.
	const queue = [...(childrenByParent.get(vpcId) ?? [])];
	for (let index = 0; index < queue.length; index += 1) {
		const node = queue[index];
		queue.push(...(childrenByParent.get(node.id) ?? []));
		if (node.type !== "insightGateway") continue;
		const family = attachmentFamilyOf(String(node.data?.kind ?? ""));
		if (!family) continue;
		const list = byFamily.get(family) ?? [];
		list.push({
			id: node.id,
			label: String(node.data?.label ?? node.id),
			nativeId: typeof node.data?.nativeId === "string" ? node.data.nativeId : undefined,
		});
		byFamily.set(family, list);
	}
	return byFamily;
}

/**
 * The VPC-level connection resources as literal nodes above the collapsed
 * card — one circular icon node per family, count on the node past 1.
 * Every member gateway's name/id rides on the node (tooltip), and clicking
 * it opens the same list in the sidebar with each entry navigable to the
 * gateway's full detail. `repackSiblingGroup`'s `rowHeadroom` reserves the
 * band they occupy, with clearance so their labels never sit under the
 * card's floating "VPC" pill.
 */
function buildVpcAttachmentNodes(
	vpcId: string,
	gatewaysByFamily: Map<VpcAttachmentFamily, Array<{ id: string; label: string; nativeId?: string }>>
): { nodes: Node<InsightNodeData>[]; stackHeight: number } {
	const present = VPC_ATTACHMENT_FAMILIES.filter(({ key }) => (gatewaysByFamily.get(key)?.length ?? 0) > 0);
	if (present.length === 0) return { nodes: [], stackHeight: 0 };
	const totalWidth = present.length * VPC_ATTACHMENT_NODE_WIDTH + (present.length - 1) * VPC_ATTACHMENT_NODE_GAP;
	const startX = (OVERVIEW_CARD_WIDTH - totalWidth) / 2;
	const nodes = present.map(({ key, label }, index) => {
		const gateways = gatewaysByFamily.get(key) as Array<{ id: string; label: string; nativeId?: string }>;
		return {
			id: `${vpcId}::attachment-${key}`,
			type: "insightVpcAttachment",
			parentId: vpcId,
			position: {
				x: startX + index * (VPC_ATTACHMENT_NODE_WIDTH + VPC_ATTACHMENT_NODE_GAP),
				y: -(VPC_ATTACHMENT_NODE_HEIGHT + VPC_ATTACHMENT_PILL_CLEARANCE),
			},
			width: VPC_ATTACHMENT_NODE_WIDTH,
			height: VPC_ATTACHMENT_NODE_HEIGHT,
			style: { width: VPC_ATTACHMENT_NODE_WIDTH, height: VPC_ATTACHMENT_NODE_HEIGHT },
			data: {
				label,
				kind: "vpc-attachment",
				ctxrUuids: [],
				attachmentFamily: key,
				attachmentCount: gateways.length,
				attachmentGateways: gateways,
			},
		};
	});
	return { nodes, stackHeight: VPC_ATTACHMENT_NODE_HEIGHT };
}

function buildAzSummaryNode(azNode: Node<InsightNodeData>, counts: ScopeCounts, box: Box): Node<InsightNodeData> {
	return {
		id: azNode.id,
		type: "insightAzSummary",
		parentId: azNode.parentId,
		position: { x: box.x, y: box.y },
		width: box.width,
		height: box.height,
		extent: azNode.extent,
		style: { width: box.width, height: box.height },
		data: { ...azNode.data, summaryCounts: counts, scopeId: azNode.id, scopeLevel: "az" },
	};
}

function buildScopeOverflowNode(
	id: string,
	parentId: string | undefined,
	hiddenIds: string[],
	scopeLevel: "vpc" | "az" | "subnet" | "regional" | "shelf",
	box: Box,
	expanded = false
): Node<InsightNodeData> {
	return {
		id,
		type: "insightScopeOverflow",
		parentId,
		position: { x: box.x, y: box.y },
		width: box.width,
		height: box.height,
		extent: parentId ? "parent" : undefined,
		style: { width: box.width, height: box.height },
		data: { label: `+${hiddenIds.length}`, kind: "scope-overflow", ctxrUuids: [], hiddenTotal: hiddenIds.length, scopeLevel, expanded },
	};
}

/**
 * Applies a cap + one-expanded-among-siblings rule to one scope level and
 * returns the bounded id lists the caller needs both to build the resulting
 * node array and to synthesize summary/overflow cards. Pure id-set math —
 * no positions/counts yet.
 */
function boundSiblingGroup(
	siblingIds: string[],
	expandedIds: ReadonlySet<string>,
	visibleLimit: number,
	bypassLimit: boolean
): { keptIds: string[]; visibleCollapsedIds: string[]; overflowHiddenIds: string[] } {
	const keptIds = siblingIds.filter((id) => expandedIds.has(id));
	const collapsedIds = siblingIds.filter((id) => !expandedIds.has(id));
	if (bypassLimit || collapsedIds.length <= visibleLimit) {
		return { keptIds, visibleCollapsedIds: collapsedIds, overflowHiddenIds: [] };
	}
	return { keptIds, visibleCollapsedIds: collapsedIds.slice(0, visibleLimit), overflowHiddenIds: collapsedIds.slice(visibleLimit) };
}

/**
 * Ancestor VPC/AZ/subnet ids of every currently-matching search result, for
 * the canvas to pass back in as `forceExpanded*`/`forceVisibleSubnetIds` so a
 * hidden match auto-reveals its whole ancestor chain (CTX-167 acceptance:
 * "search auto-reveals and focuses hidden matches"). Operates on the *full*
 * pre-collapse node list, since a match can live inside a currently-collapsed
 * branch.
 */
export function computeForceExpansionForMatches(
	nodes: Node<InsightNodeData>[],
	matchingNodeIds: ReadonlySet<string> | null
): { vpcIds: Set<string>; azIds: Set<string>; subnetIds: Set<string> } {
	const vpcIds = new Set<string>();
	const azIds = new Set<string>();
	const subnetIds = new Set<string>();
	if (!matchingNodeIds || matchingNodeIds.size === 0) return { vpcIds, azIds, subnetIds };

	const byId = new Map(nodes.map((node) => [node.id, node]));
	matchingNodeIds.forEach((matchId) => {
		let current = byId.get(matchId)?.parentId;
		const seen = new Set<string>();
		while (current && byId.has(current) && !seen.has(current)) {
			const ancestor = byId.get(current) as Node<InsightNodeData>;
			if (ancestor.type === "insightVpc") vpcIds.add(ancestor.id);
			if (ancestor.type === "insightAz") azIds.add(ancestor.id);
			if (ancestor.type === "insightSubnet") subnetIds.add(ancestor.id);
			seen.add(current);
			current = ancestor.parentId;
		}
	});
	return { vpcIds, azIds, subnetIds };
}

export function applyHierarchyDisclosure(
	nodes: Node<InsightNodeData>[],
	focus: HierarchyFocusState = DEFAULT_HIERARCHY_FOCUS,
	options: HierarchyDisclosureOptions = {}
): HierarchyDisclosureResult {
	const visibleVpcLimit = options.visibleVpcLimit ?? DEFAULT_VISIBLE_VPC_LIMIT;
	const visibleAzLimit = options.visibleAzLimit ?? DEFAULT_VISIBLE_AZ_LIMIT;
	const visibleSubnetLimit = options.visibleSubnetLimit ?? DEFAULT_VISIBLE_SUBNET_LIMIT;
	const visibleRegionalLimit = options.visibleRegionalLimit ?? DEFAULT_VISIBLE_REGIONAL_LIMIT;
	const visibleShelfLimit = options.visibleShelfLimit ?? DEFAULT_VISIBLE_SHELF_LIMIT;
	const forceExpandedVpcIds = options.forceExpandedVpcIds ?? new Set<string>();
	const forceExpandedAzIds = options.forceExpandedAzIds ?? new Set<string>();
	const forceVisibleSubnetIds = options.forceVisibleSubnetIds ?? new Set<string>();
	const forceVisibleRegionalIds = options.forceVisibleRegionalIds ?? new Set<string>();
	const overviewColumns = options.overviewColumns ?? OVERVIEW_GRID_COLUMNS;

	// Immutable-input contract: `byId` is only ever read from for the
	// *original* node/box of a given id. All positional/size overrides are
	// accumulated in `boxOverrides` and applied when the final array is
	// built — nothing in `nodes` is ever mutated in place.
	const byId = new Map(nodes.map((node) => [node.id, node]));
	const childrenByParent = new Map<string, Node<InsightNodeData>[]>();
	nodes.forEach((node) => {
		if (!node.parentId) return;
		const list = childrenByParent.get(node.parentId) ?? [];
		list.push(node);
		childrenByParent.set(node.parentId, list);
	});

	const allVpcIds = nodes.filter((node) => node.type === "insightVpc").map((node) => node.id);
	// Wider columns than the compact-overview grid for the shelf band (see the
	// shelf pass) — hoisted here because the kept-stack headroom below needs
	// the same column count to size each VPC's gateway tier.
	const shelfColumns = Math.max(overviewColumns, 6);
	/** Vertical band an expanded VPC's gateway tier occupies above its top border (0 when it has no gateways). */
	const gatewayTierHeadroomFor = (vpcId: string): number => {
		const gatewayCount = (childrenByParent.get(vpcId) ?? []).filter((child) => child.type === "insightGateway").length;
		if (gatewayCount === 0) return 0;
		const rows = Math.ceil(gatewayCount / shelfColumns);
		return gatewayTrayBandHeight(rows * (GATEWAY_TIER_CARD_HEIGHT + OVERVIEW_GRID_GAP_Y) - OVERVIEW_GRID_GAP_Y);
	};
	const allRegionalContainerIds = nodes
		.filter((node) => node.type === "insightRegionalArea" || node.type === "insightOtherArea")
		.map((node) => node.id);

	const removedIds = new Set<string>();
	const summaryNodesById = new Map<string, Node<InsightNodeData>>();
	const syntheticNodes: Node<InsightNodeData>[] = [];
	const boxOverrides = new Map<string, Box>();
	const touchedContainerIds = new Set<string>();

	const markTouched = (id: string | undefined) => idAndAncestors(id, byId).forEach((ancestorId) => touchedContainerIds.add(ancestorId));

	/**
	 * A kept (expanded) container's stacking slot must reserve the box it will
	 * actually need once the final containment pass has grown it around any
	 * child `cardLayout.ts` re-boxed past the ELK slot (e.g. a pinned AZ's
	 * subnet, whose interior no subnet-level repack ever touches) — reserving
	 * only `boxOf` puts the grown container on top of the next kept sibling.
	 * Children deliberately drawn outside the border (gateway tier, attachment
	 * nodes) are exempt, mirroring the containment pass itself.
	 */
	const requiredBoxOf = (node: Node<InsightNodeData>): Box => {
		const box = boxOf(node);
		let width = box.width;
		let height = box.height;
		(childrenByParent.get(node.id) ?? []).forEach((child) => {
			if (child.type === "insightGateway" || child.type === "insightVpcAttachment" || child.type === "insightGatewayTray") return;
			const childBox = boxOf(child);
			width = Math.max(width, child.position.x + childBox.width + CONTAINER_PADDING_RIGHT);
			height = Math.max(height, child.position.y + childBox.height + CONTAINER_PADDING_BOTTOM);
		});
		return { ...box, width, height };
	};

	// ---- VPC level ----------------------------------------------------
	const effectiveFocusedVpcId =
		allVpcIds.length <= 1 ? allVpcIds[0] ?? null : focus.focusedVpcId && allVpcIds.includes(focus.focusedVpcId) ? focus.focusedVpcId : null;

	const expandedVpcIds = new Set<string>([
		...(allVpcIds.length <= 1 ? allVpcIds : []),
		...(effectiveFocusedVpcId ? [effectiveFocusedVpcId] : []),
		...Array.from(focus.pinnedVpcIds).filter((id) => allVpcIds.includes(id)),
		...Array.from(forceExpandedVpcIds).filter((id) => allVpcIds.includes(id)),
	]);

	groupByParent(allVpcIds, byId).forEach((siblingIds) => {
		const { keptIds, visibleCollapsedIds, overflowHiddenIds } = boundSiblingGroup(siblingIds, expandedVpcIds, visibleVpcLimit, focus.vpcsExpanded);
		const collapsedIds = [...visibleCollapsedIds, ...overflowHiddenIds];

		const parentId = byId.get(siblingIds[0])?.parentId;
		// CTX-173: a regional-resources/"other" area sharing this same parent
		// (e.g. S3/Lambda placed outside any VPC) was laid out by ELK assuming
		// the full, uncollapsed VPC hierarchy still occupied its original
		// footprint. Once sibling VPCs shrink to compact summary cards, that
		// area's retained absolute position can sit far away from the new
		// compact cluster, stretching the overview's `fitView` bounds back out
		// to the original sprawling coordinate range — so it must be repacked
		// alongside the VPC grid instead of only capping its own children.
		const siblingPool = parentId ? childrenByParent.get(parentId) ?? [] : nodes.filter((sibling) => !sibling.parentId);
		const extraOverviewSiblingIds = siblingPool
			.filter((sibling) => OVERVIEW_SIBLING_CONTAINER_TYPES.has(sibling.type as InsightSemanticNodeType) && !siblingIds.includes(sibling.id))
			.map((sibling) => sibling.id);

		// Repack when anything collapsed, when a regional-area sibling needs
		// re-gridding — or when TWO OR MORE siblings are kept open: each kept
		// VPC's ELK slot assumed the original full-detail layout, so leaving
		// them there puts two simultaneously-opened VPCs at opposite ends of
		// the original sprawl (observed live 2026-08-04: both VPCs of one
		// account opened → rendered continents apart). A single kept VPC with
		// nothing collapsed stays untouched (preserves ELK layout exactly for
		// the legacy single-VPC digest).
		if (collapsedIds.length === 0 && extraOverviewSiblingIds.length === 0 && keptIds.length <= 1) return;

		markTouched(parentId);

		const keptEntries = [...keptIds, ...extraOverviewSiblingIds].map((id) => {
			const box = boxOf(byId.get(id) as Node<InsightNodeData>);
			return { id, width: box.width, height: box.height, topHeadroom: gatewayTierHeadroomFor(id) };
		});
		// Counts and per-gateway pill stacks computed up front: the tallest
		// stack in the group decides the grid's per-row headroom.
		const countsById = new Map(visibleCollapsedIds.map((id) => [id, countDescendants(id, childrenByParent)]));
		const attachmentStacksById = new Map(
			visibleCollapsedIds.map((id) => [id, buildVpcAttachmentNodes(id, collectVpcAttachmentGateways(id, childrenByParent))])
		);
		const tallestStack = Math.max(0, ...Array.from(attachmentStacksById.values(), (stack) => stack.stackHeight));
		const { positions } = repackSiblingGroup(
			visibleCollapsedIds,
			overflowHiddenIds.length > 0,
			keptEntries,
			overviewColumns,
			OVERVIEW_ORIGIN_Y,
			tallestStack > 0 ? tallestStack + VPC_ATTACHMENT_PILL_CLEARANCE + 4 : 0
		);

		collapsedIds.forEach((id) => collectDescendantIds(id, childrenByParent).forEach((descId) => removedIds.add(descId)));
		overflowHiddenIds.forEach((id) => removedIds.add(id)); // The collapsed VPC node itself is replaced by the shared overflow card, not kept as its own summary.

		visibleCollapsedIds.forEach((id) => {
			const node = byId.get(id) as Node<InsightNodeData>;
			const counts = countsById.get(id) as ScopeCounts;
			const box = positions.get(id);
			if (!box) return;
			summaryNodesById.set(id, buildVpcSummaryNode(node, counts, box));
			syntheticNodes.push(...(attachmentStacksById.get(id)?.nodes ?? []));
		});

		if (overflowHiddenIds.length > 0) {
			const box = positions.get(OVERFLOW_SLOT_KEY);
			if (box) syntheticNodes.push(buildScopeOverflowNode(`${parentId ?? "root"}::vpc-overflow`, parentId, overflowHiddenIds, "vpc", box));
		}

		keptEntries.forEach((entry) => {
			const box = positions.get(entry.id);
			if (box) boxOverrides.set(entry.id, box);
		});
	});

	// ---- VPC connectivity shelf (route tables + gateways) --------------
	// An expanded VPC's route-table/gateway cards keep whatever sparse band
	// ELK computed for the *full* card set (a real DISH VPC's 27 route tables
	// span a ~2400px-wide, ~420px-tall band with a further gap before the
	// first AZ). Same bounded-disclosure rule as ENIs/subnets: cap the
	// visible cards, dense-repack the survivors at the top of the VPC, fold
	// the rest behind one "+N" overflow card, and reclaim the vertical band
	// by shifting the content below it up. AZ/subnet repacks for the same
	// VPC stack *below* this band via their `originY` (see
	// `shelfBottomByVpc`).
	const shelfBottomByVpc = new Map<string, number>();
	expandedVpcIds.forEach((vpcId) => {
		if (removedIds.has(vpcId)) return;
		const shelf = (childrenByParent.get(vpcId) ?? []).filter((child) => SHELF_NODE_TYPES.has(child.type as string));
		// Gateways form their own deduplicated tier (each TGW/IGW/NAT drawn
		// exactly once, above the tables that use it); only the route-table
		// count is bounded by the visible cap.
		const gateways = shelf.filter((child) => child.type === "insightGateway");
		// Render priority: tables with the most proven gateway connections
		// (TGW/NAT/IGW/... chips) come first, left to right — and, since the
		// visible cap slices this same order, the capped shelf keeps the
		// most-connected tables instead of whatever the server happened to
		// list first. Ties keep the server order (Array.sort is stable).
		const connectivityWeightOf = (child: Node<InsightNodeData>): number =>
			Array.isArray(child.data?.connectivity) ? child.data.connectivity.length : 0;
		const routeTables = shelf
			.filter((child) => child.type === "insightRouteTable")
			.sort((a, b) => connectivityWeightOf(b) - connectivityWeightOf(a));
		const overCap = routeTables.length > visibleShelfLimit;
		// Unconditional for any non-empty shelf: leaving a small shelf at its
		// raw ELK positions collides with the AZ/subnet grids the passes below
		// place at the VPC's interior origin (the "broken small VPC" bug).
		if (shelf.length === 0) return;

		// `shelfExpanded` never restores the sparse ELK band: the expanded
		// state is the SAME dense top-of-VPC grid, just uncapped, with the
		// toggle card flipping to "Show fewer" — mirroring the ENI overflow's
		// expand/collapse contract instead of a one-way cap bypass.
		const expanded = focus.shelfExpanded || !overCap;
		const visibleTables = expanded ? routeTables : routeTables.slice(0, visibleShelfLimit);
		const hidden = expanded ? [] : routeTables.slice(visibleShelfLimit);
		markTouched(vpcId);
		hidden.forEach((node) => {
			removedIds.add(node.id);
			collectDescendantIds(node.id, childrenByParent).forEach((descId) => removedIds.add(descId));
		});

		// ---- Gateway tier: OUTSIDE the VPC, riding above its top border ----
		// The VPC interior is dedicated to route tables + subnets; the
		// gateways (VPC-level connections) live above the border, mirroring
		// the per-gateway pills on the collapsed summary cards. They stay
		// children of the VPC (negative y), so they move with it through
		// every later pass; the VPC itself shifts down by the tier's height
		// so the tier occupies the space the container originally started at.
		const tierRows = Math.ceil(gateways.length / shelfColumns);
		const tierHeight = gateways.length > 0 ? tierRows * (GATEWAY_TIER_CARD_HEIGHT + OVERVIEW_GRID_GAP_Y) - OVERVIEW_GRID_GAP_Y : 0;
		gateways.forEach((gateway, index) => {
			const col = index % shelfColumns;
			const row = Math.floor(index / shelfColumns);
			boxOverrides.set(gateway.id, {
				x: col * (GATEWAY_TIER_CARD_WIDTH + OVERVIEW_GRID_GAP_X),
				y: -(GATEWAY_TRAY_PAD_BOTTOM + tierHeight) + row * (GATEWAY_TIER_CARD_HEIGHT + OVERVIEW_GRID_GAP_Y),
				width: GATEWAY_TIER_CARD_WIDTH,
				height: GATEWAY_TIER_CARD_HEIGHT,
			});
		});
		// The tray band enclosing the tier: bottom edge flush on the VPC's top
		// border (y = 0), label strip above the pills, side padding around
		// them. Non-interactive (style.pointerEvents none) so the pills stay
		// clickable regardless of render order.
		if (gateways.length > 0) {
			const tierRowWidth =
				Math.min(gateways.length, shelfColumns) * (GATEWAY_TIER_CARD_WIDTH + OVERVIEW_GRID_GAP_X) - OVERVIEW_GRID_GAP_X;
			const trayWidth = tierRowWidth + 2 * GATEWAY_TRAY_PAD_X;
			const trayHeight = gatewayTrayBandHeight(tierHeight);
			syntheticNodes.push({
				id: `${vpcId}::gateway-tray`,
				type: "insightGatewayTray",
				parentId: vpcId,
				position: { x: -GATEWAY_TRAY_PAD_X, y: -trayHeight },
				width: trayWidth,
				height: trayHeight,
				selectable: false,
				style: { width: trayWidth, height: trayHeight, pointerEvents: "none" as const },
				data: {
					label: String(byId.get(vpcId)?.data?.label ?? vpcId),
					kind: "gateway-tray",
					ctxrUuids: [],
				},
			});
		}
		// A VPC positioned by the kept-stack already had its tray band reserved
		// as headroom above its slot; only a VPC still at its raw ELK position
		// (single-VPC digests where nothing collapsed) shifts itself down.
		if (tierHeight > 0 && !boxOverrides.has(vpcId)) {
			const base = boxOf(byId.get(vpcId) as Node<InsightNodeData>);
			boxOverrides.set(vpcId, { ...base, y: base.y + gatewayTrayBandHeight(tierHeight) });
		}

		// ---- Route-table grid (the interior's top band; their gateway
		// associations render as family icon chips on each card's border) ----
		const tableEntries = visibleTables.map((node) => {
			const box = boxOf(node);
			return { id: node.id, width: box.width, height: SHELF_ROUTE_TABLE_HEIGHT };
		});
		const includeToggle = overCap;
		const toggleEntry = { id: OVERFLOW_SLOT_KEY, width: OVERVIEW_CARD_WIDTH, height: SHELF_ROUTE_TABLE_HEIGHT };
		const tablePositions = repackVariableGrid(
			includeToggle ? [...tableEntries, toggleEntry] : tableEntries,
			shelfColumns,
			SHELF_TABLE_ORIGIN_Y
		);
		tableEntries.forEach((entry) => {
			const box = tablePositions.get(entry.id);
			if (box) boxOverrides.set(entry.id, box);
		});
		if (includeToggle) {
			const toggleBox = tablePositions.get(OVERFLOW_SLOT_KEY) as Box;
			syntheticNodes.push(
				buildScopeOverflowNode(`${vpcId}::shelf-overflow`, vpcId, hidden.map((node) => node.id), "shelf", toggleBox, focus.shelfExpanded)
			);
		}


		let newBandBottom = SHELF_TABLE_ORIGIN_Y;
		tablePositions.forEach((box) => {
			newBandBottom = Math.max(newBandBottom, box.y + box.height);
		});
		shelfBottomByVpc.set(vpcId, newBandBottom);

		// Keep the content below the band adjacent to it in BOTH directions:
		// collapsing reclaims the vacated space (children shift up), expanding
		// pushes them down so the now-taller grid never overlaps them. Later
		// AZ/subnet repacks simply overwrite these overrides for the ids they
		// position themselves — this shift only survives for children no other
		// pass touches (e.g. a single AZ group that never collapses).
		const origBandBottom = shelf.reduce((max, node) => {
			const box = boxOf(node);
			return Math.max(max, box.y + box.height);
		}, 0);
		const delta = origBandBottom - newBandBottom;
		if (delta !== 0) {
			(childrenByParent.get(vpcId) ?? []).forEach((child) => {
				if (SHELF_NODE_TYPES.has(child.type as string) || removedIds.has(child.id)) return;
				if (child.position.y < origBandBottom) return;
				const box = boxOf(child);
				boxOverrides.set(child.id, { ...box, y: Math.max(child.position.y - delta, newBandBottom + OVERVIEW_GRID_GAP_Y) });
			});
		}
	});

	/** Vertical origin for a repack nested inside `hostId` — below that host's compacted connectivity shelf when it has one, else `fallback` (the VPC-interior origin when the host is a VPC, whose own header text needs clearance; the plain origin inside an AZ). */
	const repackOriginYFor = (hostId: string | null | undefined, fallback: number = OVERVIEW_ORIGIN_Y): number => {
		const shelfBottom = hostId ? shelfBottomByVpc.get(hostId) : undefined;
		return shelfBottom != null ? shelfBottom + OVERVIEW_GRID_GAP_Y : fallback;
	};

	// ---- Availability-group level (inside EVERY expanded VPC) ----------
	// The focused VPC gets the focused-AZ semantics (breadcrumbs, one AZ
	// expanded); a pinned/force-expanded VPC collapses ALL its AZ groups to
	// compact summaries — leaving them at raw ELK positions produced the
	// sparse, gap-riddled pinned-VPC layout.
	let effectiveFocusedAzId: string | null = null;
	let hasAzDimension = false;
	expandedVpcIds.forEach((vpcId) => {
		if (removedIds.has(vpcId)) return;
		const isFocusedVpc = vpcId === effectiveFocusedVpcId;
		const azIds = (childrenByParent.get(vpcId) ?? []).filter((child) => child.type === "insightAz").map((child) => child.id);
		if (isFocusedVpc) hasAzDimension = azIds.length > 0;
		if (azIds.length === 0) return;

		const focusedAzForThisVpc = isFocusedVpc
			? azIds.length <= 1
				? azIds[0] ?? null
				: focus.focusedAzId && azIds.includes(focus.focusedAzId)
					? focus.focusedAzId
					: null
			: null;
		if (isFocusedVpc) effectiveFocusedAzId = focusedAzForThisVpc;
		const expandedAzIds = new Set<string>([
			...(isFocusedVpc && azIds.length <= 1 ? azIds : []),
			...(focusedAzForThisVpc ? [focusedAzForThisVpc] : []),
			...Array.from(focus.pinnedAzIds).filter((id) => azIds.includes(id)),
			...Array.from(forceExpandedAzIds).filter((id) => azIds.includes(id)),
		]);

		const { keptIds, visibleCollapsedIds, overflowHiddenIds } = boundSiblingGroup(azIds, expandedAzIds, visibleAzLimit, focus.azsExpanded);
		const collapsedIds = [...visibleCollapsedIds, ...overflowHiddenIds];
		// Reposition even when nothing collapses (e.g. a single-AZ VPC): once
		// the shelf pass re-laid the interior, the AZ's raw ELK coordinate is
		// stale — leaving it produced a dead band between the tables and a
		// faraway AZ box inside an oversized VPC.
		if (collapsedIds.length > 0 || shelfBottomByVpc.has(vpcId)) {
			markTouched(vpcId);

			const keptEntries = keptIds.map((id) => {
				const box = requiredBoxOf(byId.get(id) as Node<InsightNodeData>);
				return { id, width: box.width, height: box.height };
			});
			// Fill the width the VPC will have anyway: a kept expanded AZ (or the
			// shelf's own grid) already makes the VPC wide, so packing summaries
			// at the narrow global column count stranded a void on the right.
			const keptMaxWidth = Math.max(0, ...keptEntries.map((entry) => entry.width));
			const widthDrivenColumns = Math.floor((keptMaxWidth + OVERVIEW_GRID_GAP_X) / (AZ_SUMMARY_CARD_WIDTH + OVERVIEW_GRID_GAP_X));
			const azColumns = Math.max(shelfColumns, Math.min(12, widthDrivenColumns), overviewColumns);
			const { positions } = repackSiblingGroup(
				visibleCollapsedIds,
				overflowHiddenIds.length > 0,
				keptEntries,
				azColumns,
				repackOriginYFor(vpcId, SHELF_TABLE_ORIGIN_Y),
				0,
				AZ_SUMMARY_CARD_HEIGHT,
				AZ_SUMMARY_CARD_WIDTH
			);

			collapsedIds.forEach((id) => collectDescendantIds(id, childrenByParent).forEach((descId) => removedIds.add(descId)));
			overflowHiddenIds.forEach((id) => removedIds.add(id));

			visibleCollapsedIds.forEach((id) => {
				const node = byId.get(id) as Node<InsightNodeData>;
				const counts = countDescendants(id, childrenByParent);
				const box = positions.get(id);
				if (!box) return;
				summaryNodesById.set(id, buildAzSummaryNode(node, counts, box));
			});

			if (overflowHiddenIds.length > 0) {
				const box = positions.get(OVERFLOW_SLOT_KEY);
				if (box) {
					syntheticNodes.push(buildScopeOverflowNode(`${vpcId}::az-overflow`, vpcId, overflowHiddenIds, "az", box));
				}
			}

			keptEntries.forEach((entry) => {
				const box = positions.get(entry.id);
				if (box) boxOverrides.set(entry.id, box);
			});
		}
	});

	// ---- Subnet-count level (direct subnet children of whichever host is
	// currently expanded — the focused AZ, or the focused VPC when there's
	// no AZ dimension). Subnet *cards* themselves are never swapped for a
	// summary (a subnet card is already the compact form) — only the sibling
	// *count* is capped. CTX-173: the survivors are still repacked into a
	// dense grid (each keeping its own real width/height, only its x/y
	// moves) rather than left at their original ELK coordinates — a
	// single/few-VPC account with hundreds of subnets is exactly the
	// "massively large account" shape the VPC-level repack didn't cover, and
	// leaving survivors scattered across the *original* (pre-cap) sibling
	// count's footprint reproduces the same "container still huge" bug one
	// level down.
	const subnetHostId = hasAzDimension ? effectiveFocusedAzId : effectiveFocusedVpcId;
	if (subnetHostId && !removedIds.has(subnetHostId)) {
		// Sorted for reading, not left in discovery order — see
		// `compareSubnetsForReading`. The cap below then keeps the LOWEST-addressed
		// subnets, which is a defensible rule; discovery order was arbitrary.
		const subnetNodes = (childrenByParent.get(subnetHostId) ?? [])
			.filter((child) => child.type === "insightSubnet")
			.sort(compareSubnetsForReading);
		const subnetIds = subnetNodes.map((child) => child.id);
		const overCap = subnetIds.length > visibleSubnetLimit && !focus.subnetsExpanded;
		// A subnet whose interior was re-boxed by `cardLayout.ts` no longer fits
		// the slot ELK computed for the server's smaller card — its whole sibling
		// group must be re-gridded even when the visible-count cap isn't hit.
		const hasResizedSibling = subnetNodes.some((child) => child.data?.cardLayout === "normalized");
		if ((overCap || hasResizedSibling) && subnetIds.length > 0) {
			const forced = subnetIds.filter((id) => forceVisibleSubnetIds.has(id));
			const unforced = subnetIds.filter((id) => !forceVisibleSubnetIds.has(id));
			const remainingBudget = Math.max(visibleSubnetLimit - forced.length, 0);
			const visible = overCap ? new Set([...forced, ...unforced.slice(0, remainingBudget)]) : new Set(subnetIds);
			const hidden = subnetIds.filter((id) => !visible.has(id));

			markTouched(subnetHostId);
			hidden.forEach((id) => {
				removedIds.add(id);
				collectDescendantIds(id, childrenByParent).forEach((descId) => removedIds.add(descId));
			});

			const visibleEntries = subnetIds
				.filter((id) => visible.has(id))
				.map((id) => {
					const box = boxOf(byId.get(id) as Node<InsightNodeData>);
					return { id, width: box.width, height: box.height };
				});
			const overflowEntry = { id: OVERFLOW_SLOT_KEY, width: OVERVIEW_CARD_WIDTH, height: OVERVIEW_CARD_HEIGHT };
			const positions = repackVariableGrid(
				hidden.length > 0 ? [...visibleEntries, overflowEntry] : visibleEntries,
				overviewColumns,
				// Inside an AZ the plain origin clears its small floating label;
				// directly inside a VPC (no AZ dimension) the grid must clear the
				// VPC's own header text.
				repackOriginYFor(subnetHostId, hasAzDimension ? OVERVIEW_ORIGIN_Y : SHELF_TABLE_ORIGIN_Y),
				// Subnet cards are the one group whose widths genuinely differ, each
				// sized around its own ENI grid.
				true
			);
			visibleEntries.forEach((entry) => {
				const box = positions.get(entry.id);
				if (box) boxOverrides.set(entry.id, box);
			});
			if (hidden.length > 0) {
				const overflowBox = positions.get(OVERFLOW_SLOT_KEY) as Box;
				syntheticNodes.push(buildScopeOverflowNode(`${subnetHostId}::subnet-overflow`, subnetHostId, hidden, "subnet", overflowBox));
			}
		}
	}

	// ---- Regional resources (S3/Lambda/... outside any VPC) -----------
	// CTX-173: same survivor-repack treatment as subnets above — a regional
	// area with dozens of ungrouped services otherwise leaves survivors at
	// their original ELK coordinates and the overflow card at the first
	// hidden item's stale position.
	allRegionalContainerIds.forEach((areaId) => {
		const items = (childrenByParent.get(areaId) ?? []).filter((child) => child.type === "insightRegionalService").map((child) => child.id);
		if (items.length <= visibleRegionalLimit || focus.regionalExpanded) return;
		const forced = items.filter((id) => forceVisibleRegionalIds.has(id));
		const unforced = items.filter((id) => !forceVisibleRegionalIds.has(id));
		const remainingBudget = Math.max(visibleRegionalLimit - forced.length, 0);
		const visible = new Set([...forced, ...unforced.slice(0, remainingBudget)]);
		const hidden = items.filter((id) => !visible.has(id));
		if (hidden.length === 0) return;

		markTouched(areaId);
		hidden.forEach((id) => removedIds.add(id));

		const visibleEntries = items
			.filter((id) => visible.has(id))
			.map((id) => {
				const box = boxOf(byId.get(id) as Node<InsightNodeData>);
				return { id, width: box.width, height: box.height };
			});
		const overflowEntry = { id: OVERFLOW_SLOT_KEY, width: OVERVIEW_CARD_WIDTH, height: OVERVIEW_CARD_HEIGHT };
		const positions = repackVariableGrid([...visibleEntries, overflowEntry], overviewColumns);
		visibleEntries.forEach((entry) => {
			const box = positions.get(entry.id);
			if (box) boxOverrides.set(entry.id, box);
		});
		const overflowBox = positions.get(OVERFLOW_SLOT_KEY) as Box;
		syntheticNodes.push(buildScopeOverflowNode(`${areaId}::regional-overflow`, areaId, hidden, "regional", overflowBox));
	});

	// ---- Region/Provider siblings (CTX-173 round 2) -------------------
	// Every level above checks its *children* against a visible-count cap;
	// none of them ever repositions the *region* or *provider* container
	// itself among its own siblings. A region/provider keeps whatever
	// absolute coordinate ELK assigned it — sized for the original,
	// uncollapsed whole-account layout — even after every VPC inside it has
	// collapsed to a tiny compact summary card. A real account spanning
	// multiple regions (or a reflector aggregating multiple provider
	// accounts) then keeps those regions/providers scattered exactly as ELK
	// spread them across the *original* canvas: each region's own VPC grid
	// is now small and dense, but the account overview's `fitView` still has
	// to frame the empty gaps ELK left between the regions' original
	// far-apart positions, reproducing the "tiny dots, massive empty canvas"
	// bug one level above where the VPC/AZ/subnet/regional-service logic
	// above already fixed it (that logic covers one region's own
	// *children*; this covers a region's position among its *own*
	// siblings). Detected up front (on the *original* `nodes`, before any
	// VPC-level capping) so a bare multi-region/-provider account with no
	// capped VPCs anywhere still gets repositioned, not just accounts big
	// enough to also trip a VPC/AZ/subnet cap.
	const hasMultiRegionSiblings = Array.from(
		groupByParent(nodes.filter((node) => node.type === "insightRegion").map((node) => node.id), byId).values()
	).some((ids) => ids.length > 1);
	const hasMultiProviderSiblings = Array.from(
		groupByParent(nodes.filter((node) => node.type === "insightProvider").map((node) => node.id), byId).values()
	).some((ids) => ids.length > 1);

	if (
		removedIds.size === 0 &&
		summaryNodesById.size === 0 &&
		syntheticNodes.length === 0 &&
		boxOverrides.size === 0 &&
		!hasMultiRegionSiblings &&
		!hasMultiProviderSiblings
	) {
		return { nodes, effectiveFocusedVpcId, effectiveFocusedAzId, hasAzDimension };
	}

	const kept: Node<InsightNodeData>[] = nodes
		.filter((node) => !removedIds.has(node.id))
		.map((node) => {
			const summary = summaryNodesById.get(node.id);
			if (summary) return summary;
			const box = boxOverrides.get(node.id);
			if (!box) return node;
			// A box override carries width/height too (the shelf pass slims its
			// route-table cards) — equal to the node's own size everywhere else,
			// so applying them unconditionally is a no-op for every other pass.
			return {
				...node,
				position: { x: box.x, y: box.y },
				width: box.width,
				height: box.height,
				style: { ...node.style, width: box.width, height: box.height },
			};
		});

	const preRefit = [...kept, ...syntheticNodes];

	// Bottom-up refit: recompute width/height for every touched container so
	// it hugs its (possibly now much smaller) children's bounding box instead
	// of keeping its original ELK-computed footprint — "must not leave giant
	// empty spaces". Only containers in `touchedContainerIds` are considered;
	// every untouched container (the common case) is returned byte-for-byte
	// unchanged. Processed deepest-first so an outer container's bounding box
	// already reflects its own children's post-refit sizes.
	const finalById = new Map(preRefit.map((node) => [node.id, node]));
	const finalChildrenByParent = new Map<string, Node<InsightNodeData>[]>();
	preRefit.forEach((node) => {
		if (!node.parentId) return;
		const list = finalChildrenByParent.get(node.parentId) ?? [];
		list.push(node);
		finalChildrenByParent.set(node.parentId, list);
	});

	const sizeOverrides = new Map<string, { width: number; height: number }>();
	const depthOf = (id: string) => idAndAncestors(id, finalById).length;
	const orderedContainerIds = Array.from(touchedContainerIds)
		.filter((id) => finalById.has(id) && INSIGHT_CONTAINER_NODE_TYPES.has(finalById.get(id)?.type as InsightSemanticNodeType))
		.sort((a, b) => depthOf(b) - depthOf(a));

	orderedContainerIds.forEach((id) => {
		const children = finalChildrenByParent.get(id);
		if (!children || children.length === 0) return;
		let maxRight = 0;
		let maxBottom = 0;
		children.forEach((child) => {
			const override = sizeOverrides.get(child.id);
			const width = override?.width ?? (typeof child.width === "number" ? child.width : 0);
			const height = override?.height ?? (typeof child.height === "number" ? child.height : 0);
			maxRight = Math.max(maxRight, child.position.x + width);
			maxBottom = Math.max(maxBottom, child.position.y + height);
		});
		sizeOverrides.set(id, { width: maxRight + CONTAINER_PADDING_RIGHT, height: maxBottom + CONTAINER_PADDING_BOTTOM });
	});

	// Containment safety net (browser-observed escape, 2026-08-04): the
	// passes above only repack/refit the containers they explicitly touched —
	// a container that kept its ELK (or kept-slot) box while a child grew
	// underneath it (e.g. a PINNED, non-focused AZ whose subnet `cardLayout.ts`
	// re-boxed for the 184x88 ENI grid — the subnet repack only ever runs for
	// the *focused* AZ) ends up visually smaller than its own child. Enforce
	// the invariant structurally: every container GROWS to contain its
	// in-border children (never shrinks — hugging remains the touched-refit's
	// job). Children deliberately drawn outside the border (gateway tier,
	// attachment family nodes) are exempt. Deepest-first so a grown inner
	// container feeds the outer one's requirement.
	const outsideBorderChildTypes = new Set<string>(["insightGateway", "insightVpcAttachment", "insightGatewayTray"]);
	preRefit
		.filter((node) => INSIGHT_CONTAINER_NODE_TYPES.has(node.type as InsightSemanticNodeType) && (finalChildrenByParent.get(node.id)?.length ?? 0) > 0)
		.map((node) => node.id)
		.sort((a, b) => depthOf(b) - depthOf(a))
		.forEach((id) => {
			let maxRight = 0;
			let maxBottom = 0;
			(finalChildrenByParent.get(id) ?? []).forEach((child) => {
				if (outsideBorderChildTypes.has(child.type ?? "")) return;
				const override = sizeOverrides.get(child.id);
				const width = override?.width ?? (typeof child.width === "number" ? child.width : 0);
				const height = override?.height ?? (typeof child.height === "number" ? child.height : 0);
				maxRight = Math.max(maxRight, child.position.x + width);
				maxBottom = Math.max(maxBottom, child.position.y + height);
			});
			const requiredWidth = maxRight + CONTAINER_PADDING_RIGHT;
			const requiredHeight = maxBottom + CONTAINER_PADDING_BOTTOM;
			const current = sizeOverrides.get(id) ?? boxOf(finalById.get(id) as Node<InsightNodeData>);
			if (requiredWidth > current.width || requiredHeight > current.height) {
				sizeOverrides.set(id, { width: Math.max(requiredWidth, current.width), height: Math.max(requiredHeight, current.height) });
			}
		});

	// Reposition region siblings first, then provider siblings — in that
	// order, so that when a provider's own children (regions) get repacked,
	// each region's effective size already reflects both its own VPC-level
	// refit above AND, for a provider with a single region, nothing extra;
	// for a provider whose *regions* also needed repacking, the freshly
	// packed region positions/sizes below feed straight into the provider
	// pass's own `sizeOverrides` lookup.
	const positionOverrides = new Map<string, { x: number; y: number }>();
	(["insightRegion", "insightProvider"] as InsightSemanticNodeType[]).forEach((type) => {
		const idsOfType = preRefit.filter((node) => node.type === type).map((node) => node.id);
		groupByParent(idsOfType, finalById).forEach((siblingIds, parentId) => {
			if (siblingIds.length <= 1) return; // nothing to reposition against
			const entries = siblingIds.map((id) => {
				const sibling = finalById.get(id) as Node<InsightNodeData>;
				const box = boxOf(sibling);
				const override = sizeOverrides.get(id);
				return { id, width: override?.width ?? box.width, height: override?.height ?? box.height };
			});
			const positions = repackVariableGrid(entries, overviewColumns);
			entries.forEach((entry) => {
				const box = positions.get(entry.id);
				if (box) positionOverrides.set(entry.id, { x: box.x, y: box.y });
			});
			if (parentId === "__root__" || !finalById.has(parentId)) return; // top-level siblings — nothing to size, only reposition
			let maxRight = 0;
			let maxBottom = 0;
			entries.forEach((entry) => {
				const box = positions.get(entry.id);
				if (!box) return;
				maxRight = Math.max(maxRight, box.x + box.width);
				maxBottom = Math.max(maxBottom, box.y + box.height);
			});
			sizeOverrides.set(parentId, { width: maxRight + CONTAINER_PADDING_RIGHT, height: maxBottom + CONTAINER_PADDING_BOTTOM });
		});
	});

	const finalNodes = preRefit.map((node) => {
		const size = sizeOverrides.get(node.id);
		const position = positionOverrides.get(node.id);
		if (!size && !position) return node;
		return {
			...node,
			position: position ?? node.position,
			width: size?.width ?? node.width,
			height: size?.height ?? node.height,
			style: size ? { ...node.style, width: size.width, height: size.height } : node.style,
		};
	});

	return { nodes: finalNodes, effectiveFocusedVpcId, effectiveFocusedAzId, hasAzDimension };
}
